import type { Dispatch } from 'react';
import { useEffect } from 'react';

import type { AdditionalFilterInDropdown, SelectedFiltersValues } from '../AdditionalFilters/constants';
import {
  isAdditionalBooleanFilterValue,
  isCalendarDateFilterValue,
  isLineItemEndDateFilterFilled,
  isLineItemEndDateFilterValue,
  isRangeFilterFilled,
  isRangeFilterValue,
} from '../AdditionalFilters/utils';

export type UseSyncSelectedDropdownFiltersProps = {
  filters: AdditionalFilterInDropdown[];
  selectedFiltersValues: SelectedFiltersValues;
  onSelectedDropdownFilters: Dispatch<React.SetStateAction<AdditionalFilterInDropdown[]>>;
};

/**
 * Keeps the `selectedDropdownFilters` chip registry in sync with applied
 * `selectedFiltersValues` from `FilterContext`. A filter definition is added
 * to the registry whenever its applied value is non-null/non-empty for its
 * type (boolean, range, line item end date, or list).
 */
export const useSyncSelectedDropdownFilters = ({
  filters,
  selectedFiltersValues,
  onSelectedDropdownFilters,
}: UseSyncSelectedDropdownFiltersProps): void => {
  useEffect(() => {
    onSelectedDropdownFilters(
      filters.filter((filter) => {
        const selectedValues = selectedFiltersValues[filter.searchParam];

        if (isAdditionalBooleanFilterValue(filter, selectedValues)) return selectedValues != null;

        if (isRangeFilterValue(selectedValues)) return isRangeFilterFilled(selectedValues);

        if (isLineItemEndDateFilterValue(selectedValues) || isCalendarDateFilterValue(selectedValues))
          return isLineItemEndDateFilterFilled(selectedValues);

        return Array.isArray(selectedValues) && selectedValues.length > 0;
      })
    );
  }, [selectedFiltersValues, filters, onSelectedDropdownFilters]);
};
